import { md5 } from './crypto'
import { Cache } from './cache'
import { sharePromise } from './share-promise'
import { request } from './request'

const CDN_HOST = 'https://zhiyi-image.oss-cn-hangzhou.aliyuncs.com/'
const CDN_DIR = 'zhi-xiao-bu-upload/'
interface UploadResult {
  sign: {
    accessid: number
    signature: string
    policy: string
    host: string
  }
}
async function generateFileName(content: File, dir: string = '', mime?: string) {
  const hashName = await md5(content)
  return `${CDN_DIR}${dir ? dir + '/' : ''}${hashName}${mime || '.png'}`
}

// 创建token缓存实例
const tokenCache = new Cache<{ host: string; token: Record<string, string> }>({ maxItems: 1 })

async function initToken() {
  // 尝试从缓存获取
  const cachedToken = tokenCache.get('ossToken')
  if (cachedToken) {
    return cachedToken
  }

  const result = (await request.get('/api/upload/get-oss-upload-token', {
    params: { dir: CDN_DIR },
  })) as UploadResult
  if (!result?.sign) {
    throw new Error('获取上传 token 失败')
  }
  const res = result.sign
  const token = {
    OSSAccessKeyId: res.accessid,
    signature: res.signature,
    policy: res.policy,
  } as unknown as Record<string, string>
  const tokenData = { host: res.host, token }
  // 设置缓存，60秒过期
  tokenCache.set('ossToken', tokenData, 60 * 1000, 'absolute')
  return tokenData
}
const shareInitToken = sharePromise(initToken)
/**
 * 上传文件
 * @param file 文件
 * @param dir 目录 例如：'user/avatar' 末尾不要斜杠
 * @param mime 文件类型 例如：'.png'
 * @param onProgress 进度回调函数，返回0-100的进度百分比
 * @returns 文件url
 */
export async function upload(file: File, dir: string = '', mime = '.png', onProgress?: (percent: number) => void) {
  const res = await shareInitToken()
  const key = await generateFileName(file, dir, mime)

  const formData = new FormData()
  Object.entries(res.token).forEach(([k, v]) => {
    formData.append(k, v as string)
  })
  formData.append('success_action_status', '200')
  formData.append('key', key)
  formData.append('file', file)

  // 使用XMLHttpRequest来支持进度回调
  await new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest()

    // 监听上传进度
    xhr.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable) {
        const percent = Math.round((event.loaded / event.total) * 100)
        onProgress?.(percent)
      }
    })

    // 设置完成和错误处理
    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve()
      } else {
        reject(new Error(`上传失败: ${xhr.status} ${xhr.statusText}`))
      }
    })

    xhr.addEventListener('error', () => {
      reject(new Error('网络错误，上传失败'))
    })

    xhr.addEventListener('abort', () => {
      reject(new Error('上传已取消'))
    })

    // 打开并发送请求
    xhr.open('POST', res.host)
    xhr.send(formData)
  })

  return CDN_HOST + key
}

export function base64ToBlob(base64Uri: string) {
  const arr = base64Uri.split(',')
  const mime = arr[0].match(/:(.*?);/)?.[1]
  const bstr = atob(arr[1])
  const u8arr = new Uint8Array(bstr.length)

  for (let n = bstr.length; n--; ) {
    u8arr[n] = bstr.charCodeAt(n)
  }

  return new Blob([u8arr], { type: mime })
}
